import React, { Suspense, useState, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  getItem,
  getItemCommerce,
  itemIsAuctionItem,
  itemIsCentralStorage,
} from '../../selectors/item';
import Contact from './contact';
import Insurance from './insurance';
import Price from './price';
import Seller from './seller';

import usePayments from '../../Data/Payments/usePayments';
import DateTime, { dateTimeFormat, type DateTimeFormats } from '../../components/date-time';
import { RenderClientSide } from '../../components/render';
import { getMarket } from '../../i18n';
import { StackTiny } from '../../styled-components/components/Stack';
import { Text } from '../../styled-components/components/Text';
import { VStack } from '../../styled-components/components/VStack';
import AssignmentWidget from '../assignments/CreateAssignment/Widget';
import BusinessLeasing from './BusinessLeasing';
import PrivateLeasing from './PrivateLeasing';
import CentralStorage from './central-storage';
import PaymentOptions from './payment-options';
import Payments from './payments';

const EcomButton = lazy(() => import('../../components/ecom-button-v2/EcomButtonV2'));
const FinancialModal = lazy(() => import('./financial-modal'));
const InsuranceModal = lazy(() => import('./insurance-modal'));
const CentralStorageModal = lazy(() => import('./central-storage-modal'));

const ConvertCardBanner = ({
  logo,
  title,
}: { logo?: string; title: string }): React.ReactElement => (
  <div className="convert-card-banner">
    {logo && <i className={`icon-${logo} m-r-mini`} />}
    {title}
  </div>
);

interface PropsType {
  id: string;
  contactSectionRef: React.Ref<HTMLDivElement>;
}

const Card = ({ id, contactSectionRef }: PropsType) => {
  const { t } = useTranslation(['common', 'item']);
  const item = useSelector(getItem);
  const isCentralStorage = useSelector(itemIsCentralStorage);
  const isAuctionItem = useSelector(itemIsAuctionItem);
  const commerce = useSelector(getItemCommerce);
  // const financialInformation = useSelector(getFinancialInformation);
  const { marketCode } = getMarket();
  const [payments] = usePayments(id, item?.branches?.[0]?.id);

  const [modalFinancial, setModalFinancial] = useState(false);
  const [modalInsurance, setModalInsurances] = useState(false);
  const [modalCentralStorage, setCentralStorageModal] = useState(false);

  const shouldShowEcomButton = !!commerce?.enabled && !commerce.disabledOnWayke;
  const shouldShowEcomAsReserved = shouldShowEcomButton && commerce.reserved;

  const showAssignmentWidget = marketCode === 'SE';

  if (!item || item.id !== id) return null;

  const isCurrentYear = new Date().getFullYear() === new Date(item.itemPublished).getFullYear();
  const dateFormat = isCurrentYear ? (dateTimeFormat.DateTime as DateTimeFormats) : undefined;

  return (
    <>
      {modalFinancial && (
        <Suspense>
          <FinancialModal onClose={() => setModalFinancial(false)} />
        </Suspense>
      )}
      {modalInsurance && (
        <Suspense>
          <InsuranceModal onClose={() => setModalInsurances(false)} />
        </Suspense>
      )}
      {modalCentralStorage && (
        <Suspense>
          <CentralStorageModal onClose={() => setCentralStorageModal(false)} />
        </Suspense>
      )}
      <div className="item-section item-section-convert-card">
        <div className="item-section-convert-card-part">
          <div data-wx-convertcard="">
            {shouldShowEcomAsReserved && (
              <ConvertCardBanner title={t('Reserved', { ns: 'item' })} />
            )}
            {isCentralStorage && (
              <ConvertCardBanner title={t('Central storage', { ns: 'item' })} logo="globe" />
            )}
            {isAuctionItem && (
              <ConvertCardBanner title={t('Auction item', { ns: 'item' })} logo="auction" />
            )}
            <div className="convert-card-body">
              <Price inventoryStatus={item.inventoryStatusString} />
              <div className="convert-card-section">
                <VStack $spacing={1} ref={contactSectionRef}>
                  {shouldShowEcomButton && (
                    <RenderClientSide
                      skeleton={
                        <button
                          type="button"
                          data-wx-button="primary full-width"
                          title={t('Buy online', { ns: 'item' })}
                          disabled={shouldShowEcomAsReserved}
                        >
                          <div className="button-icon">
                            <i className="icon-cart no-margin" />
                          </div>
                          <div className="button-text">{t('Buy online', { ns: 'item' })}</div>
                        </button>
                      }
                    >
                      <EcomButton id={item.id} disabled={shouldShowEcomAsReserved} />
                    </RenderClientSide>
                  )}
                  <Contact />
                </VStack>
              </div>
              {(!!item.leasingPrice ||
                !!item.businessLeasingPrice ||
                //!!financialInformation ||
                !!payments?.length) && (
                <div className="convert-card-section">
                  <StackTiny>
                    <Text $weight="medium" $size="xsmallStatic">
                      {t('Financing and ownership options', { ns: 'item' })}
                    </Text>
                  </StackTiny>
                  <StackTiny>
                    <PrivateLeasing />
                    <BusinessLeasing />
                    {/*<Financial onClick={() => setModalFinancial(true)} />*/}
                    {payments && item.branches?.[0]?.id && (
                      <Payments
                        item={item}
                        payments={payments}
                        id={item.id}
                        branchId={item.branches[0].id}
                      />
                    )}
                  </StackTiny>
                </div>
              )}
            </div>
            <Insurance onClick={() => setModalInsurances(true)} />
            <div className="convert-card-body text-center font-size-tiny-rem text-dark-lighten">
              {t('published', { ns: 'common' })}{' '}
              <DateTime value={item.itemPublished} format={dateFormat} />
            </div>
            <Seller />
            <PaymentOptions options={item.paymentOptions} />
            <CentralStorage onClick={() => setCentralStorageModal(true)} />
          </div>
        </div>
        {showAssignmentWidget && (
          <div className="item-section-convert-card-part">
            <AssignmentWidget
              variant="productPage"
              trackingContext={{
                type: 'widget',
                placement: 'productPage',
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Card;
